/**
 * Application version.
 *
 * Resolution order (single source of truth = git tag):
 *   1. Browser bundles: replaced at build time by Vite `define` with
 *      `__APP_VERSION__` (set from `resolveAppVersion()` in
 *      packages/shared/scripts/resolve-version.mjs).
 *   2. Node API: read from `process.env.APP_VERSION` at runtime (set by
 *      the deploy workflow from the release tag).
 *   3. Fallback: `'v0.0.0-dev'` — surfaces obvious noise if injection is
 *      ever missing, instead of silently shipping a stale literal.
 */
declare const __APP_VERSION__: string | undefined;
// Minimal ambient `process` so this browser-shared package typechecks without
// pulling in @types/node (TS 6 no longer resolves the Node global implicitly).
declare const process: { env?: Record<string, string | undefined> } | undefined;

function resolveAppVersion(): string {
  if (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) return __APP_VERSION__;
  if (typeof process !== 'undefined' && process.env?.APP_VERSION) return process.env.APP_VERSION;
  return 'v0.0.0-dev';
}

export const APP_VERSION = resolveAppVersion();

/** Warranty calculation percentage (3% of services revenue) */
export const WARRANTY_PERCENTAGE = 0.03;

/** Default page size for ConnectWise API pagination */
export const PAGE_SIZE = 100;

/** Maximum pages to fetch before stopping */
export const MAX_PAGES = 100;

/** Rate limit: requests per window */
export const RATE_LIMIT_MAX = 300;

/** Rate limit: window in milliseconds (15 minutes) */
export const RATE_LIMIT_WINDOW_MS = 15 * 60 * 1000;

/** Rate limit for file uploads */
export const UPLOAD_RATE_LIMIT_MAX = 10;

/** Max file upload size in bytes (10 MB) */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Max per-file upload size for sub-onboarding documents (COI, W-9, bank verify).
 * Raised from 10 MB → 15 MB after a real submitter saw a generic 500 from a
 * phone-camera photo of a cancelled check that exceeded multer's old cap.
 */
export const SUB_ONBOARDING_MAX_FILE_MB = 15;
export const SUB_ONBOARDING_MAX_FILE_SIZE = SUB_ONBOARDING_MAX_FILE_MB * 1024 * 1024;

/** Max configuration name length (CW API requirement) */
export const MAX_CONFIG_NAME_LENGTH = 100;

/** Max MAC address length (CW API requirement) */
export const MAX_MAC_ADDRESS_LENGTH = 25;

/** ConnectWise configuration defaults */
export const CW_DEFAULTS = {
  CONFIGURATION_TYPE_ID: 28, // Customer Care Card
  CONFIGURATION_STATUS_ID: 2, // Active
  LOCATION_ID: 2,
  WARRANTY_AGREEMENT_TYPE_ID: 43,
  INTERNAL_AGREEMENT_TYPE_ID: 44,
  DEFAULT_BILLING_CYCLE_ID: 2,
  /**
   * "Not Billed" (billingOptions: NotRecurring). Warranty agreements MUST use this.
   * A recurring cycle plus any non-zero header billAmount makes ConnectWise invoice the
   * customer every period — see the 2026-08 incident, where 16 warranty agreements on a
   * Bi-Monthly cycle billed $7,897.92 to 13 customers. All 735 agreements created by the
   * hts-warranty service use this cycle; the portal was hardcoding Bi-Monthly (id 1).
   */
  WARRANTY_BILLING_CYCLE_ID: 8,
  DEFAULT_WORK_ROLE_RATE: 145,
  SERVICE_RATE_AGREEMENT_NAME: 'HTS - Service Rates',
} as const;

/** PDF certificate constants */
export const PDF = {
  MAX_PRODUCT_DESC_LENGTH: 40,
  MAX_PROJECT_NAME_LENGTH: 55,
  MAX_PROJECT_NAME_FOOTER_LENGTH: 40,
  MAX_PM_NAME_LENGTH: 50,
  MAX_PM_SIGNATURE_LENGTH: 30,
  COMPANY_NAME: 'Harris Technology Services',
  CHARS_PER_LINE: 80,
  MIN_FIELD_HEIGHT: 60,
  LINE_HEIGHT: 15,
  FIELD_PADDING: 10,
  FIELD_VERTICAL_OFFSET: 15,
  MAX_ACCEPTANCE_TEXT_CHARS: 90,
  LOGO_WIDTH: 120,
  LOGO_HEIGHT: 40,
} as const;

/** PM time as percentage of total services revenue */
export const PM_TIME_PERCENTAGE = 0.07;

/** PM labor rates */
export const PM_SELL_RATE = 195; // PM sell price per hour
export const PM_COST_RATE = 97.5; // PM cost per hour

/** Internal tech labor rates */
export const TECH_SELL_RATE = 145; // Tech sell price per hour
export const TECH_COST_RATE = 72; // Tech cost per hour

/**
 * PM hourly rate for PM ticket budget hours. Mirrors DEFAULT_FINANCE_RATES
 * .pmBurdenRate, which converts the PM time carve-out into PM hours in both
 * budget.service and projectReview.
 */
export const PM_HOURLY_RATE_PM = PM_COST_RATE;

/** Device info notes header */
export const DEVICE_INFO_HEADER = '=== Device Information ===';

/** Predefined subcontractor skills */
export const PREDEFINED_SKILLS = [
  'Low Voltage Cabling',
  'Cameras',
  'Access Control',
  'Locksmith Services',
  'IT Services',
  'Structured Cabling',
  'Audio/Visual',
  'Fire Alarm',
  'Electrical',
  'Networking',
  'Complicated Installs',
  'General Construction',
] as const;

/** Subcontractor onboarding submission statuses */
export const ONBOARDING_STATUSES = [
  'submitted',
  'docusign_sent',
  'docusign_sub_signed',
  'docusign_signed',
  'docusign_declined',
  'docusign_voided',
  'under_review',
  'approved',
  'rejected',
] as const;

export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

/** Purchase receipt approval statuses */
export const APPROVAL_STATUSES = ['pending', 'pm_approved', 'approved', 'rejected', 'paid'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

/** Deployment workflow statuses */
export const DEPLOYMENT_STATUSES = [
  'pending',
  'customer_kickoff',
  'materials',
  'in_progress',
  'completed',
] as const;

export type DeploymentStatusType = (typeof DEPLOYMENT_STATUSES)[number];

/** Deployment workflow step names */
export const DEPLOYMENT_STEPS = [
  'Customer Kickoff',
  'Material Ordering',
  'Closeout',
] as const;

/** Sub invoice statuses (partner → PM → Ops → CW receiving → QB payment) */
export const SUB_INVOICE_STATUSES = [
  'submitted',
  'pm_approved',
  'ops_approved',
  'received',
  'paid',
  'rejected',
] as const;

export type SubInvoiceStatus = (typeof SUB_INVOICE_STATUSES)[number];

/** Verkada order match statuses */
export const VERKADA_ORDER_STATUSES = [
  'pending',
  'matched',
  'partial_match',
  'discrepancy',
  'resolved',
] as const;

export type VerkadaOrderStatus = (typeof VERKADA_ORDER_STATUSES)[number];

/** Policy categories */
export const POLICY_CATEGORIES = [
  'health_safety',
  'cyber_security',
  'hr_employment',
  'it_acceptable_use',
  'operations',
  'financial',
  'legal_compliance',
] as const;

export type PolicyCategory = (typeof POLICY_CATEGORIES)[number];

/** Human-readable labels for policy categories */
export const POLICY_CATEGORY_LABELS: Record<PolicyCategory, string> = {
  health_safety: 'Health & Safety',
  cyber_security: 'Cyber Security',
  hr_employment: 'HR & Employment',
  it_acceptable_use: 'IT & Acceptable Use',
  operations: 'Operations',
  financial: 'Financial',
  legal_compliance: 'Legal & Compliance',
};

/** Policy statuses */
export const POLICY_STATUSES = ['draft', 'published', 'archived'] as const;

export type PolicyStatus = (typeof POLICY_STATUSES)[number];

/** Max size for inline policy images (5 MB) */
export const POLICY_IMAGE_MAX_SIZE = 5 * 1024 * 1024;

/** Max size for document imports (20 MB) */
export const POLICY_IMPORT_MAX_SIZE = 20 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Safety Reporting & ISNetworld Compliance
// ---------------------------------------------------------------------------

/** OSHA rate multiplier: rates are per 200,000 hours worked */
export const OSHA_RATE_MULTIPLIER = 200_000;

/** Safety incident types */
export const SAFETY_INCIDENT_TYPES = [
  'injury', 'illness', 'near_miss', 'property_damage', 'vehicle_accident', 'environmental',
] as const;

export type SafetyIncidentType = (typeof SAFETY_INCIDENT_TYPES)[number];

/** Human-readable labels for incident types */
export const SAFETY_INCIDENT_TYPE_LABELS: Record<SafetyIncidentType, string> = {
  injury: 'Injury',
  illness: 'Illness',
  near_miss: 'Near Miss',
  property_damage: 'Property Damage',
  vehicle_accident: 'Vehicle Accident',
  environmental: 'Environmental',
};

/** Safety incident severity levels */
export const SAFETY_INCIDENT_SEVERITIES = [
  'first_aid', 'recordable', 'lost_time', 'restricted_duty', 'fatality',
] as const;

export type SafetyIncidentSeverity = (typeof SAFETY_INCIDENT_SEVERITIES)[number];

/** Human-readable labels for severity levels */
export const SAFETY_INCIDENT_SEVERITY_LABELS: Record<SafetyIncidentSeverity, string> = {
  first_aid: 'First Aid',
  recordable: 'Recordable',
  lost_time: 'Lost Time',
  restricted_duty: 'Restricted Duty',
  fatality: 'Fatality',
};

/** Safety incident statuses */
export const SAFETY_INCIDENT_STATUSES = ['open', 'investigating', 'closed'] as const;

export type SafetyIncidentStatus = (typeof SAFETY_INCIDENT_STATUSES)[number];

/** Safety training categories */
export const SAFETY_TRAINING_CATEGORIES = [
  'osha', 'cyber', 'fall_protection', 'hazcom', 'first_aid', 'cpr',
  'forklift', 'electrical', 'ppe', 'driving', 'confined_space', 'lockout_tagout', 'other',
] as const;

export type SafetyTrainingCategory = (typeof SAFETY_TRAINING_CATEGORIES)[number];

/** Human-readable labels for training categories */
export const SAFETY_TRAINING_CATEGORY_LABELS: Record<SafetyTrainingCategory, string> = {
  osha: 'OSHA',
  cyber: 'Cybersecurity',
  fall_protection: 'Fall Protection',
  hazcom: 'Hazard Communication',
  first_aid: 'First Aid',
  cpr: 'CPR/AED',
  forklift: 'Forklift',
  electrical: 'Electrical Safety',
  ppe: 'PPE',
  driving: 'Defensive Driving',
  confined_space: 'Confined Space',
  lockout_tagout: 'Lockout/Tagout',
  other: 'Other',
};

/** Project status descriptions and requirements for tooltip display */
export const PROJECT_STATUS_DESCRIPTIONS: Record<string, string> = {
  'Sold': 'Project has been sold. Requirements: signed contract, project number assigned.',
  'Pre-Construction': 'Preparing for project kickoff. Requirements: site survey completed, materials list finalized.',
  'Scheduled': 'Project is scheduled for installation. Requirements: materials ordered, crew assigned, customer notified.',
  'Material Ordering': 'Ordering equipment and materials. Requirements: BOM approved, POs created.',
  'Pending Equipment Delivery': 'Awaiting equipment/materials delivery before scheduling. Requirements: POs placed, delivery/tracking confirmed.',
  'Material Staging': 'Materials received and being staged. Requirements: all materials received and verified.',
  'In Progress': 'Active installation work underway. Requirements: crew on-site, daily progress updates.',
  'Punch List': 'Installation complete, addressing final items. Requirements: walkthrough completed, punch list documented.',
  'Closeout': 'Project wrapping up. Requirements: all punch items resolved, as-builts submitted.',
  'Completed': 'Project is finished. Requirements: customer sign-off, final invoice sent.',
  'Closed': 'Project fully closed. Requirements: all invoices paid, warranty registered.',
  'Construction Hold': 'On hold due to construction delays. Site not ready for installation.',
  'Customer Hold': 'On hold per customer request. Awaiting customer decision or action.',
  'Internal Hold': 'On hold due to internal reasons. May need resource reallocation or management review.',
  'On Hold': 'Project is paused. Review hold reason and requirements before resuming.',
};

/**
 * CW catalog item ID used for "Subcontract Labor" line items on projects.
 * Used to filter sub-labor entries out of customer-facing documents (e.g., COC).
 */
export const SUBCONTRACT_LABOR_PRODUCT_ID = 2232;

/** US states for form dropdowns */
export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
] as const;

// #298 — canonical finance rates. Single source of truth for the previously
// drifted tech-labor rate (SQL path 72, API fallback 75, budget 72 → unified 72).
export const DEFAULT_FINANCE_RATES = {
  techBurdenRate: 72,        // internal tech labor cost/hr (and budget→hours conversion)
  // Converts the PM time carve-out (pmTimePercent% of revenue) into PM budget
  // hours (budget.service + projectReview PM-hours). COST basis: the kickoff
  // budget is a cost screen, so PM hours = PM allocation ÷ what PM time costs
  // (7% of $61.7k = $4,319 → 44h @ $97.50, not 22h @ the $195 sell rate).
  // Supersedes #282's sell-rate basis (2026-09-03 product decision); kickoff
  // and projectReview still tie out because both divide by this same field.
  pmBurdenRate: PM_COST_RATE,
  pmTimePercent: PM_TIME_PERCENTAGE, // 0.07 — PM overhead % of sold labor
  warrantyPercent: WARRANTY_PERCENTAGE, // 0.03 — warranty % of sold labor
  changeOrderBurdenRate: 72, // CO estimate-fallback labor cost/hr (defaults to techBurdenRate)
  blendedSellRate: 150,      // blended SELL rate/hr for expected-revenue estimates (CW-API-only ASC 606 fallback)
  monthlyBillingGoal: 2000000, // ops monthly billing target ($) — drives the OpsDashboard goal bar + chart goal line (#681)
} as const;

/**
 * The pre-settings-era defaults, frozen. Every kickoff budget calculated before
 * rate snapshots existed was computed with pmBurdenRate = 195 (the #282 sell
 * basis). When a stored row has NULL snapshot columns — or an API predating the
 * snapshot fields omits them — label those budgets with THESE values, not the
 * current defaults: claiming a legacy 22h budget was calculated at a 97.50
 * cost basis would be a false record. New calculations always get explicit
 * snapshots and never touch this.
 */
export const LEGACY_FINANCE_RATES = {
  ...DEFAULT_FINANCE_RATES,
  pmBurdenRate: PM_SELL_RATE, // 195 — #282 sell basis, superseded 2026-09-03
} as const;

/**
 * Payroll overtime & timezone policy (#630, #631).
 *
 * HTS overtime is **weekly-only**: hours beyond {@link WEEKLY_OT_THRESHOLD_HOURS}
 * cumulative within a single (Eastern) ISO week are overtime. There is NO daily
 * (>8h/day) component — a 10h day in an otherwise-empty week is 10 regular / 0 OT.
 *
 * Payroll day/week bucketing uses {@link PAYROLL_TIMEZONE} (the app's canonical
 * business timezone — matches `User.timezone` default and digest-timing) so a
 * late-evening Eastern entry never slides into the next UTC day/week. Both the
 * Gusto export and the field-execution engine allocate OT through the SAME
 * shared primitives (`allocateWeeklyOvertime`, `easternDateKey`,
 * `easternIsoWeekMondayKey`) so the paycheck and the WorkDaySummary can never
 * diverge again.
 */
export const WEEKLY_OT_THRESHOLD_HOURS = 40;

/** IANA timezone that all payroll day/week bucketing is anchored to. */
export const PAYROLL_TIMEZONE = 'America/New_York';

export const BASELINE_TEAMS = ['NOC', 'Field', 'PM'] as const;
export type BaselineTeam = (typeof BASELINE_TEAMS)[number];

export const LABOR_SOURCES = ['internal', 'sub', 'inherit'] as const;
export type LaborSource = (typeof LABOR_SOURCES)[number];

// RMA lifecycle statuses (#301). Canonical forward order — a record advances
// (or skips) forward through these; `closed` is terminal. The portal RmaRecord
// owns this lifecycle; transitions are also mirrored onto the CW rmaTag status
// (#611 — CW rmaTags are patchable after create, contrary to earlier belief).
export const RMA_STATUSES = [
  'open',
  'return_shipped',
  'replacement_received',
  'credit_pending',
  'closed',
] as const;
export type RmaStatus = (typeof RMA_STATUSES)[number];

// PM task lifecycle (#605). `pending` is the create/seed default (see the PMTask
// Prisma default and plan.service); `completed`/`cancelled` are terminal (they
// stop due-date nudges — see task-notifications DONE_STATUSES). Status drives the
// execution rollup and PM-dashboard filters, so it MUST be one of these — an
// arbitrary string silently breaks those rollups.
export const PM_TASK_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'] as const;
export type PmTaskStatus = (typeof PM_TASK_STATUSES)[number];

// PM task priority (#605). `normal` is the default; `high`/`urgent` raise the
// assignment-alert severity and render dashboard badges (see task-notifications
// and PMDashboard). `low` is the conventional bottom tier.
export const PM_TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type PmTaskPriority = (typeof PM_TASK_PRIORITIES)[number];

// Task dependency relation types (#578, schedule-layer foundation). Standard PDM
// (precedence-diagramming) set: Finish-to-Start, Start-to-Start, Finish-to-Finish,
// Start-to-Finish. The model carries all four now; #579's scheduling math starts
// with FS+lag. `FS` is the default when a dependency omits a type.
export const TASK_DEPENDENCY_TYPES = ['FS', 'SS', 'FF', 'SF'] as const;
export type TaskDependencyType = (typeof TASK_DEPENDENCY_TYPES)[number];

// Human warranty-vs-billable call recorded per ticket (#301). `assessCoverage`
// only assesses; a person decides and that decision drives billing routing.
export const WARRANTY_DECISIONS = ['covered', 'billable'] as const;
export type WarrantyDecision = (typeof WARRANTY_DECISIONS)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Project RAID log (#577) — Risks, Assumptions, Issues, Decisions
// ─────────────────────────────────────────────────────────────────────────────

// One `ProjectRaidItem` table, discriminated by `type` (roadmap D1). Risks also
// carry likelihood + impact; issues carry a dueDate; decisions carry decidedAt.
export const RAID_ITEM_TYPES = ['risk', 'issue', 'assumption', 'decision'] as const;
export type RaidItemType = (typeof RAID_ITEM_TYPES)[number];

// Shared RAID lifecycle. `open` is the default. `mitigating` = actively being
// worked (typical for risks/issues); `closed` = resolved/retired; `accepted` =
// consciously tolerated (a risk we accept, an assumption we lock in, a decision
// we ratify). Any string other than these silently breaks the health rollup and
// grouped counts, so it MUST be one of these.
export const RAID_STATUSES = ['open', 'mitigating', 'closed', 'accepted'] as const;
export type RaidStatus = (typeof RAID_STATUSES)[number];

// Risk-only qualifiers. Likelihood × impact is the conventional risk matrix; a
// `high` impact OPEN risk is what feeds project health (#577).
export const RAID_LIKELIHOODS = ['low', 'medium', 'high'] as const;
export type RaidLikelihood = (typeof RAID_LIKELIHOODS)[number];

export const RAID_IMPACTS = ['low', 'medium', 'high'] as const;
export type RaidImpact = (typeof RAID_IMPACTS)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Project milestones / stage gates (#654)
// ─────────────────────────────────────────────────────────────────────────────

// A `ProjectMilestone` marks a stage gate on a project (kickoff, design sign-off,
// go-live, closeout…). Milestones are template-driven per projectType (mirroring
// PMTaskTemplate/ChecklistTemplate) and carry an ADVISORY gate criterion — v1
// only WARNS, it never hard-blocks a project from advancing.
//
// Lifecycle: `pending` is the default (target date set, not yet reached).
// `met` = achieved (actualDate stamped); `missed` = the gate was not satisfied;
// `waived` = consciously skipped/ N/A for this project. Any string other than
// these breaks the overdue rollup and grouped counts, so it MUST be one of these.
export const MILESTONE_STATUSES = ['pending', 'met', 'missed', 'waived'] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

// Only a `pending` milestone can be overdue — a met/missed/waived milestone is
// already resolved and never counts against the schedule signal (#654).
export const MILESTONE_OPEN_STATUSES = ['pending'] as const;

// Sub-invoice review anomalies (advisory): an invoice dated this many days or
// more BEFORE its submission is flagged as backdated for the reviewer. Future
// dates are always flagged. Shared so the API check and the web label agree.
export const SUB_INVOICE_BACKDATE_WARN_DAYS = 90;
